/* eslint-disable no-loop-func */
import FileSaver from 'file-saver';
import JSZip from 'jszip';
import { cloneDeep, forEach, isArray, keyBy, mapValues } from 'lodash';
import { Bert } from 'meteor/themeteorchef:bert';

import { importAnnotations } from '../api/Annotations/annotation-methods-client';
import * as payloadOps from '../api/Payloads/payload-operations';
import * as rpaOps from '../api/RPAs/rpa-operations';

import methodPromise from './method-promise-wrapper';
import './promise-all-keys';

const ANNOTATIONS_KEY = 'annotations';

/**
 * Downloads a text string to a local file. You can't use this method to download
 * multiple files. The preferred method for that is to create a zip file.
 *
 * @param {string} filename - The name for the file to be dowloaded with extension.
 * @param {string} text - The content for the file, must be plain text with charset UTF8.
 */
export const exportTextToLocalFile = (filename, text) => {
  const elevationProfileBlob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  FileSaver.saveAs(elevationProfileBlob, filename);
};

/**
 * Downloads a zip with files generated from text strings.
 *
 * @param {string} filename - The name for the file to be dowloaded with extension.
 * @param {Array} filesArray - The content for the file. Each item in the array must be an object:
 *        {
 *           filename: 'name for the file.extension',
 *           content: 'string with the contents for the file'
 *        }.
 */
export const exportFilesToZip = (filename, filesArray) => {
  const zipFile = new JSZip();
  forEach(filesArray, file => {
    zipFile.file(file.filename, file.content);
  });
  zipFile.generateAsync({ type: 'blob' }).then(content => {
    FileSaver.saveAs(content, filename);
  });
};

/**
 * Imports a project and all its related data contained in an object.
 *
 * @async
 * @param {object} variableWithObjects - The variable with the project content to import.
 */
export const importProject = async variableWithObjects => {
  const { project, missions = [], rpas = [], payloads = [] } = variableWithObjects;

  project.name = `${project.name} - import`;

  try {
    const newProjectId = await methodPromise('projects.insert', project);

    const payloadsPromises = mapValues(
      keyBy(payloads, payload => payloadOps.getId(payload)),
      payload => {
        const newPayload = cloneDeep(payload);
        delete newPayload._id;
        return methodPromise('payloads.insert', newPayload);
      }
    );
    const newPayloadIds = await Promise.allKeys(payloadsPromises);

    const rpasPromises = mapValues(
      keyBy(rpas, rpa => rpaOps.getId(rpa)),
      rpa => {
        const newRPA = cloneDeep(rpa);
        delete newRPA._id;
        return methodPromise('rpas.insert', newRPA);
      }
    );
    const newRPAsIds = await Promise.allKeys(rpasPromises);

    forEach(missions, mission => {
      const newMission = cloneDeep(mission);
      delete newMission._id;
      newMission.project = newProjectId;
      newMission.payload = newPayloadIds[mission.payload];
      newMission.rpa = newRPAsIds[mission.rpa];
      methodPromise('missions.import', newMission);
    });
  } catch (error) {
    Bert.alert(`Hey You have an error: ${error}`, 'danger');
  }
};

/**
 * Reads a file as text.
 *
 * @param {File} inputFile - The file that comes from the HTML5 file input.
 * @returns {Promise.<string>} The file contents.
 * @throws DOMException error if something goes wrong.
 */
export const readUploadedFileAsText = inputFile => {
  const temporaryFileReader = new FileReader();

  return new Promise((resolve, reject) => {
    temporaryFileReader.onerror = () => {
      temporaryFileReader.abort();
      reject(new DOMException('Problem parsing input file.'));
    };

    temporaryFileReader.onload = () => {
      resolve(temporaryFileReader.result);
    };
    temporaryFileReader.readAsText(inputFile);
  });
};

/**
 * Reads a file and imports it's contents as a project and it's data into the Mongo Database.
 *
 * @async
 * @param {File} file - The file that comes from the HTML5 file input.
 * @throws TypeError error if something goes wrong.
 */
export const importFromProjectFile = async file => {
  try {
    const fileContents = await readUploadedFileAsText(file);
    const parsedText = JSON.parse(fileContents);
    await importProject(parsedText);
  } catch (error) {
    throw new TypeError("Oops, we haven't got JSON!");
  }
};

/**
 * Reads a file and imports it's contents as anotations and it's data into the Mongo Database.
 *
 * @async
 * @param {object} arg - The arguments object.
 * @param {File} arg.file - The file that comes from the HTML5 file input.
 * @param {string} arg.videoId - The video ID to add the annotations to.
 * @param {boolean} [arg.spatialSync=false] - Flag to synchronize annotations by location or time.
 * @throws TypeError error if something goes wrong.
 */
export const importFromAnnotationsFile = async ({
  file,
  videoId,
  spatialSync = false,
  syncTimeGPS = false,
  annotationsProps = []
}) => {
  try {
    const fileContents = await readUploadedFileAsText(file);
    const parsedText = JSON.parse(fileContents);
    await importAnnotations({
      variableWithObjects: parsedText,
      videoId,
      spatialSync,
      syncTimeGPS,
      annotationsProps
    });
  } catch (error) {
    throw new TypeError("Oops, we haven't got JSON!");
  }
};

/**
 * Cleans the project data to export, so we don't expose data.
 *
 * @param {object} itemToClean - An object that contains the project data to export.
 * @modifies itemToClean
 */
/* eslint-disable no-param-reassign */
export const cleanObjectForExport = itemToClean => {
  forEach(itemToClean, (item, key) => {
    if (!isArray(item)) {
      delete item._id;
      delete item.createdAt;
      delete item.updatedAt;
      delete item.owner;
      delete item.deleted;
      delete item.done;
      delete item.shared;
      delete item.publicId;
    } else {
      forEach(item, arrayItem => {
        delete arrayItem.createdAt;
        delete arrayItem.updatedAt;
        delete arrayItem.owner;
        delete arrayItem.deleted;
        delete arrayItem.done;
        delete arrayItem.shared;
        delete arrayItem.publicId;
        delete arrayItem.project;
        if (key === ANNOTATIONS_KEY) {
          delete arrayItem.geoJSON.properties.createdAt;
          delete arrayItem.geoJSON.properties.updatedAt;
          delete arrayItem.geoJSON.properties.owner;
          delete arrayItem.geoJSON.properties.deleted;
          delete arrayItem.geoJSON.properties.video;
          delete arrayItem.geoJSON.properties._id;
        }
      });
    }
  });
};
/* eslint-enable no-param-reassign */
